"use client";

import { InfiniteScroll } from "@cmp-src/components/InfiniteScroll/InfiniteScroll";
import { SearchInput } from "@cmp-src/controls/SearchInput/SearchInput";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives/Popover/Popover";
import { IUserSelectable, UserRoles } from "@gettactic/api";
import { useUserListPaginated } from "@gettactic/hooks";
import React, { useState } from "react";

export interface AsyncUserPickerProps {
  /** Element that opens the picker; rendered via Radix `asChild`. */
  trigger: React.ReactNode;
  onSelect: (user: IUserSelectable) => void;
  /** Users already picked; hidden from the results. */
  excludeUserIds?: string[];
  roles?: UserRoles[];
  placeholder?: string;
  emptyLabel?: string;
  align?: "start" | "center" | "end";
}

const PAGE_SIZE = 20;

const LoadingRow: React.FC = () => (
  <p className="px-3 py-3 text-center text-neutral-400 text-xs">Loading…</p>
);

/**
 * People picker that searches the org roster **server-side** as you type, so
 * organizations larger than a single page stay fully searchable. Prefer this
 * over filtering a pre-fetched user list, which can only ever match the users
 * that happened to come back in the first request.
 */
export const AsyncUserPicker: React.FC<AsyncUserPickerProps> = ({
  trigger,
  onSelect,
  excludeUserIds = [],
  roles,
  placeholder = "Search by name, email, organization",
  emptyLabel = "No people found",
  align = "end"
}) => {
  const searchInputId = React.useId();
  const [isOpen, setIsOpen] = useState(false);
  // SearchInput debounces internally, so this already is the debounced value.
  const [query, setQuery] = useState("");
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const userList = useUserListPaginated(query, PAGE_SIZE, {}, roles);
  const { hasNextPage, fetchNextPage, isFetchingNextPage } = userList;

  const excluded = new Set(excludeUserIds);
  const candidates = userList.allPages.filter((user) => !excluded.has(user.id));

  const isLoading = userList.isFetching && candidates.length === 0;

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setQuery("");
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} showArrow={false} className="w-80 p-0">
        <div className="border-neutral-100 border-b p-2">
          <SearchInput
            inputId={searchInputId}
            query={query}
            setQuery={setQuery}
            placeholder={placeholder}
            fullWidth
          />
        </div>
        <div ref={scrollRef} className="max-h-72 overflow-y-auto py-1">
          {candidates.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => onSelect(user)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-neutral-50"
            >
              <UserAvatar user={user} size="xs" />
              <div className="min-w-0">
                <div className="truncate font-medium text-neutral-900 text-sm">
                  {user.name}
                </div>
                {user.email ? (
                  <div className="truncate text-neutral-500 text-xs">
                    {user.email}
                  </div>
                ) : null}
              </div>
            </button>
          ))}

          {/* `excludeUserIds` can filter an entire page down to nothing while
              later pages still hold selectable users — only call it empty
              once there's no more to fetch. */}
          {candidates.length === 0 && !isLoading && !hasNextPage ? (
            <p className="px-3 py-6 text-center text-neutral-400 text-sm">
              {emptyLabel}
            </p>
          ) : null}

          {isLoading ? <LoadingRow /> : null}

          {hasNextPage ? (
            <InfiniteScroll
              scrollRef={scrollRef}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              offset={100}
              spinner={<LoadingRow />}
            />
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
